// src/components/CottageSummaryOverlay.jsx
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  X,
  FileText,
  Calendar,
  BadgeCheck,
  BadgeX,
  Layers,
  Hash,
  Wallet,
  ReceiptText,
  AlertTriangle,
} from "lucide-react";
import { formatNumber } from "../utils/numberFormat";

const money = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return formatNumber(v);
};

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const StatPill = ({ icon: Icon, label, value, subtle }) => (
  <div
    className={[
      "rounded-2xl border px-4 py-3",
      subtle ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white",
    ].join(" ")}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs text-slate-500 text-right">{label}</div>
      {Icon ? <Icon className="w-4 h-4 text-slate-400" /> : null}
    </div>
    <div className="mt-1 text-sm font-semibold text-slate-900 text-right">
      {value ?? "—"}
    </div>
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="text-xs text-slate-500 whitespace-nowrap">{label}</div>
    <div className="text-sm text-slate-900 text-right break-words">
      {children}
    </div>
  </div>
);

const BoolChip = ({ value, label }) => (
  <span
    className={[
      "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs border",
      value
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-rose-50 text-rose-700 border-rose-200",
    ].join(" ")}
  >
    {value ? (
      <BadgeCheck className="w-3.5 h-3.5" />
    ) : (
      <BadgeX className="w-3.5 h-3.5" />
    )}
    {label}
  </span>
);

const CottageSummaryOverlay = ({ isOpen, onClose, cottage, customer }) => {
  // Scroll lock + ESC close
  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const proforma = cottage?.proforma;

  const goods = useMemo(
    () => (Array.isArray(cottage?.cottage_goods) ? cottage.cottage_goods : []),
    [cottage]
  );

  const mismatch =
    cottage?.customs_value != null &&
    cottage?.customs_value_epl != null &&
    String(cottage.customs_value) !== String(cottage.customs_value_epl);

  const goodsTotals = useMemo(() => {
    const sum = {
      quantity: 0,
      customs_value: 0,
      import_rights: 0,
      added_value: 0,
      other_expense: 0,
      final_price: 0,
      discount: 0,
    };

    for (const g of goods) {
      sum.quantity += safeNum(g.quantity);
      sum.customs_value += safeNum(g.customs_value);
      sum.import_rights += safeNum(g.import_rights);
      sum.added_value += safeNum(g.added_value);
      sum.other_expense += safeNum(g.other_expense);
      sum.final_price += safeNum(g.final_price);
      sum.discount += safeNum(g.discount);
    }

    const codes = new Set(
      goods.map((g) => String(g.goodscode || "").trim()).filter(Boolean)
    );

    const desc = goods
      .map((g) => String(g.goods_description || "").trim())
      .filter(Boolean);

    const uniqueDesc = Array.from(new Set(desc)).slice(0, 3);

    return { sum, hsCount: codes.size, topDescriptions: uniqueDesc };
  }, [goods]);

  if (!isOpen) return null;

  const cottageNumber = cottage?.cottage_number;

  return (
    <div
      className="fixed inset-0 z-[9999]"
      dir="rtl"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        role="button"
        aria-label="بستن"
      />

      {/* Wrapper */}
      <div className="absolute inset-0 flex items-end sm:items-center justify-center p-0 sm:p-6">
        {/* Panel */}
        <div
          onClick={(e) => e.stopPropagation()}
          className={[
            "w-full",
            // Desktop fit: bounded width + bounded height
            "sm:w-[min(900px,calc(100vw-3rem))]",
            "max-h-[92vh] sm:max-h-[84vh]",
            // Mobile: bottom sheet; Desktop: centered modal
            "rounded-t-3xl sm:rounded-3xl",
            "bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden flex flex-col",
            // Mobile slide-up animation
            "animate-[sheetUp_220ms_cubic-bezier(0.2,0.8,0.2,1)] sm:animate-none",
          ].join(" ")}
        >
          <style>{`
            @keyframes sheetUp {
              from { transform: translateY(100%); }
              to   { transform: translateY(0); }
            }
          `}</style>

          {/* Sticky header */}
          <div className="sticky top-0 z-10 px-5 py-4 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-2xl bg-white border border-slate-200 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-700" />
                </div>

                <div className="text-right">
                  <div className="text-sm font-extrabold text-slate-900">
                    خلاصه کوتاژ{" "}
                    {cottageNumber ? (
                      <Link
                        to={`/cottages/${cottageNumber}`}
                        className="text-blue-700 hover:underline"
                      >
                        {cottageNumber}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    اطلاعات کلیدی و خلاصه کالاها
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center w-11 h-11 rounded-2xl hover:bg-white transition active:scale-[0.98]"
                aria-label="بستن"
              >
                <X className="w-5 h-5 text-slate-700" />
              </button>
            </div>

            {mismatch ? (
              <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 mt-0.5" />
                <div className="text-right">
                  <div className="font-semibold">عدم تطابق ارزش گمرکی</div>
                  <div className="mt-1 text-xs">
                    ثبت‌شده: {money(cottage?.customs_value)} — EPL:{" "}
                    {money(cottage?.customs_value_epl)}
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto overscroll-contain p-5">
            {/* Top stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <StatPill
                icon={Wallet}
                label="ارزش کل"
                value={money(cottage?.total_value)}
              />
              <StatPill
                icon={ReceiptText}
                label="ارزش گمرکی"
                value={money(cottage?.customs_value)}
              />
              <StatPill
                icon={Layers}
                label="ارزش افزوده"
                value={money(cottage?.added_value)}
              />
              <StatPill
                icon={ReceiptText}
                label="هزینه‌ها"
                value={money(cottage?.total_expenses)}
              />
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
              <StatPill
                icon={Calendar}
                label="تاریخ کوتاژ"
                value={cottage?.cottage_date || "—"}
                subtle
              />
              <StatPill
                icon={Calendar}
                label="تاریخ پرداخت"
                value={cottage?.cottage_payment_date || "—"}
                subtle
              />
              <StatPill
                icon={Hash}
                label="شماره مرجع"
                value={cottage?.refrence_number || "—"}
                subtle
              />
              <StatPill
                icon={Wallet}
                label="نرخ ارز"
                value={
                  cottage?.currency_price
                    ? `${cottage.currency_price} ریال`
                    : "—"
                }
                subtle
              />
            </div>

            {/* General details */}
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-sm font-extrabold text-slate-900 text-right mb-3">
                جزئیات عمومی
              </div>

              <div className="space-y-3">
                <Row label="مشتری">
                  {customer ? (
                    <Link
                      className="text-blue-700 hover:underline"
                      to={`/customers/details/${customer.id}`}
                    >
                      {customer.full_name}
                    </Link>
                  ) : (
                    "—"
                  )}
                </Row>
                <Row label="وضعیت کوتاژ">{cottage?.cottage_status || "—"}</Row>
                <Row label="واسطه (Intermediary)">
                  {cottage?.Intermediary || "—"}
                </Row>

                <Row label="ثبت سفارش">
                  {cottage?.is_patteh ? (
                    "پته"
                  ) : proforma?.prf_order_no ? (
                    <Link
                      className="text-blue-700 hover:underline"
                      to={`/order-details/${proforma?.prfVCodeInt}`}
                    >
                      {proforma.prf_order_no}
                    </Link>
                  ) : (
                    "—"
                  )}
                </Row>

                <Row label="شماره پرفورم">{proforma?.prf_number || "—"}</Row>

                <Row label="وضعیت‌ها">
                  <div className="flex flex-wrap gap-2 justify-start">
                    <BoolChip
                      value={!!cottage?.rafee_taahod}
                      label="رفع تعهد"
                    />
                    <BoolChip
                      value={!!cottage?.docs_recieved}
                      label="اخذ مدارک"
                    />
                    <BoolChip value={!!cottage?.rewatch} label="بازبینی" />
                    <BoolChip value={!!cottage?.exited} label="خروج شده" />
                  </div>
                </Row>
              </div>
            </div>

            {/* Goods summary */}
            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-extrabold text-slate-900 text-right">
                  خلاصه کالاها
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700">
                  {goods.length} آیتم
                </span>
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
                <StatPill
                  icon={Hash}
                  label="HS Code یکتا"
                  value={goodsTotals.hsCount}
                  subtle
                />
                <StatPill
                  icon={Layers}
                  label="جمع مقدار"
                  value={formatNumber(goodsTotals.sum.quantity)}
                  subtle
                />
                <StatPill
                  icon={ReceiptText}
                  label="جمع حقوق ورودی"
                  value={formatNumber(goodsTotals.sum.import_rights)}
                  subtle
                />
                <StatPill
                  icon={ReceiptText}
                  label="جمع قیمت نهایی"
                  value={formatNumber(goodsTotals.sum.final_price)}
                  subtle
                />
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatPill
                  label="جمع ارزش گمرکی (کالاها)"
                  value={formatNumber(goodsTotals.sum.customs_value)}
                />
                <StatPill
                  label="جمع ارزش افزوده (کالاها)"
                  value={formatNumber(goodsTotals.sum.added_value)}
                />
                <StatPill
                  label="جمع سایر هزینه‌ها"
                  value={formatNumber(goodsTotals.sum.other_expense)}
                />
              </div>

              {goodsTotals.topDescriptions.length > 0 && (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs font-semibold text-slate-700 text-right mb-2">
                    نمونه شرح کالا (تا ۳ مورد)
                  </div>
                  <ul className="list-disc pr-5 text-sm text-slate-800 space-y-1 text-right">
                    {goodsTotals.topDescriptions.map((d, i) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Goods table */}
              {goods.length > 0 && (
                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-right text-slate-700">
                          <th className="p-3 font-semibold">کد کالا</th>
                          <th className="p-3 font-semibold">شرح</th>
                          <th className="p-3 font-semibold">مقدار</th>
                          <th className="p-3 font-semibold">ارزش گمرکی</th>
                          <th className="p-3 font-semibold">حقوق ورودی</th>
                          <th className="p-3 font-semibold">قیمت نهایی</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {goods.slice(0, 20).map((g) => (
                          <tr
                            key={g.id}
                            className="hover:bg-slate-50 transition"
                          >
                            <td className="p-3 font-medium text-slate-900 whitespace-nowrap">
                              {g.goodscode || "—"}
                            </td>
                            <td className="p-3 text-slate-800 min-w-[260px]">
                              {g.goods_description || "—"}
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              {formatNumber(g.quantity)}
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              {formatNumber(g.customs_value)}
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              {formatNumber(g.import_rights)}
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              {formatNumber(g.final_price)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {goods.length > 20 && (
                    <div className="bg-slate-50 px-4 py-2 text-xs text-slate-600 text-right">
                      فقط ۲۰ ردیف اول نمایش داده شده است. (کل: {goods.length})
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="h-2" />
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 flex items-center justify-start">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition"
            >
              بستن
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CottageSummaryOverlay;
